import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import TeamLogo from './TeamLogo';
import ResponsiveTeamName from './ResponsiveTeamName';
import HelperFunctions from '../helpers/HelperFunctions';
import { durationFormatter } from '../helpers/dateUtils';
import { setMinuteAlignedInterval } from '../helpers/timing';


const formatUntilKickoff = (dateStr) => {
    if (!dateStr) return '';
    const diffMs = new Date(dateStr).getTime() - Date.now();
    if (diffMs <= 0) return 'Kicking off';
    return `${durationFormatter(dateStr, { withAgo: false })} until kickoff`;
};

const formatPct = (pct) => {
    if (pct === null || pct === undefined || pct === '') return '—';
    const n = Math.floor(Number(pct));
    return Number.isNaN(n) ? '—' : `${n}%`;
};

// Count how many of the 6 markets we predict for this fixture, using the same
// scoreline-driven logic as AllPredictionsSlider. The 6 markets are:
// correct score, BTTS, over 0.5, over 1.5, over 2.5 goals and outcome.
// Correct score and outcome always have a prediction, so they always count.

const countPredictedMarkets = (fixture) => {
    const prediction = fixture?.prediction;
    const predictedScore = fixture?.top3_scorelines?.[0]?.score
        || prediction?.top3_scorelines?.[0]?.score
        || prediction?.top3_scorelines?.[0]?.score
        || fixture?.top3_scorelines?.[0]?.score
        || "0-0";

    const [homeGoalsStr, awayGoalsStr] = predictedScore.split("-");
    const homeGoals = parseInt(homeGoalsStr, 10) || 0;
    const awayGoals = parseInt(awayGoalsStr, 10) || 0;
    const expectedGoals = homeGoals + awayGoals;
    const btts = homeGoals >= 1 && awayGoals >= 1;

    return 1 // correct score
        + 1 // outcome
        + (btts ? 1 : 0)
        + (expectedGoals >= 0.5 ? 1 : 0)
        + (expectedGoals >= 1.5 ? 1 : 0)
        + (expectedGoals >= 2.5 ? 1 : 0);
};

// Expected goals, the analysis-framed alternative to a win percentage. One decimal: the
// feed gives two (1.43) and the extra digit reads as false precision at card size.
const formatXg = (xg) => {
    if (xg === null || xg === undefined || xg === '') return '—';
    const n = Number(xg);
    return Number.isNaN(n) ? '—' : `${n.toFixed(1)} xG`;
};

const TeamRow = ({ team, value }) => (
    <div className="d-flex align-items-center justify-content-between w-100 gap-2">
        <div className="d-flex align-items-center gap-2 overflow-hidden">
            <TeamLogo team={team} alt={team?.name} className="object-fit-contain rounded-1 flex-shrink-0" style={{ width: 30, height: 20 }} />
            <span className="fw-semi-bold text-white text-truncate"><ResponsiveTeamName team={team} minSize="full" /></span>
        </div>
        <span className="fw-semi-bold text-white flex-shrink-0">{value}</span>
    </div>
);

// The paid landing pages (see DemandGenLandingPage.js) need this exact card with none of
// its prediction vocabulary: Google Ads' automated review reads "markets predicted" /
// "See predictions" / win percentages as gambling. Rather than fork the layout, every
// piece of content is overridable and metric="xg" swaps the win percentage for expected
// goals. All defaults reproduce the original card exactly, so existing callers are
// untouched.
const MarketPredictedCard = ({
    fixture,
    markets = null,
    metric = 'win-pct',
    badgeLabel = null,
    ctaLabel = 'Get predictions',
    href = null,
}) => {
    // Re-render on each wall-clock minute so the "until kickoff" countdown stays
    // current (same minute-aligned tick Fixtures uses for its kickoff polling).
    const [, setTick] = useState(0);
    useEffect(() => setMinuteAlignedInterval(() => setTick((t) => t + 1)), []);

    const { slugifyForUrl } = HelperFunctions();

  
    if (!fixture) return null;

    const { home_team, away_team, match_date, prediction, fixture_id } = fixture;

    const homePct = fixture.home_win_pct ?? prediction?.home_win_pct;
    const awayPct = fixture.away_win_pct ?? prediction?.away_win_pct;
   
    const marketsPredicted = markets ?? countPredictedMarkets(fixture);
    const badge = badgeLabel ?? `${marketsPredicted} markets predicted`;

    const homeValue = metric === 'xg' ? formatXg(prediction?.home_xg) : formatPct(homePct);
    const awayValue = metric === 'xg' ? formatXg(prediction?.away_xg) : formatPct(awayPct);

    const fixtureHref = href ?? (fixture_id
        ? `/fixtures-preview/${fixture_id}/${slugifyForUrl(`${home_team?.name || ''} vs ${away_team?.name || ''}`)}`
        : '/fixtures-preview');

    return (
        <Link
            to={fixtureHref}
            className="d-flex flex-column gap-2half w-100 p-3 rounded-4 text-decoration-none h-100 translate-icon-on-hover"
            style={{ background: 'rgba(255,255,255,0.08)' }}
        >
            <div className="d-flex align-items-center justify-content-between w-100 gap-2">
                <span className="small" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatUntilKickoff(match_date)}</span>
                <span className="bg-sk-green-10 text-sk-green extra-extra-small fw-semi-bold rounded-pill px-2 py-1 text-nowrap">
                    {badge}
                </span>
            </div>

            <div className="d-flex flex-column gap-2half w-100">
                <TeamRow team={home_team} value={homeValue} />
                <TeamRow team={away_team} value={awayValue} />
            </div>

            <div className="d-flex align-items-center justify-content-end w-100 gap-2 small">
                <span className="btn-sk-nav py-2 px-3 fw-semi-bold extra-small text-black">{ctaLabel} <i className='icon fa-light fa-arrow-right'></i></span>
            </div>
        </Link>
    );
};

export default MarketPredictedCard;
